import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { LucideIcon } from 'lucide-react';

export interface Section {
  id: string;
  labelKey: string;
  icon?: LucideIcon;
}

interface SectionNavigationProps {
  sections: Section[];
  contentRef?: React.RefObject<HTMLElement | null>;
  className?: string;
}

const ROOT_MARGIN = '-20% 0px -70% 0px';
const PIN_TOP_OFFSET_PX = 16;

function usePinnedSectionNav() {
  const placeholderRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const [isPinned, setIsPinned] = useState(false);
  const [navHeight, setNavHeight] = useState(0);
  const [pinStyle, setPinStyle] = useState<{ top: number; left: number; width: number } | null>(
    null,
  );

  useEffect(() => {
    const update = () => {
      const placeholder = placeholderRef.current;
      const nav = navRef.current;
      if (!placeholder) {
        return;
      }

      if (nav) {
        setNavHeight(nav.offsetHeight);
      }

      const rect = placeholder.getBoundingClientRect();
      const shouldPin = rect.top <= PIN_TOP_OFFSET_PX;

      setIsPinned(shouldPin);
      setPinStyle(
        shouldPin
          ? {
              top: PIN_TOP_OFFSET_PX,
              left: rect.left,
              width: rect.width,
            }
          : null,
      );
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;

    if (resizeObserver) {
      if (placeholderRef.current) {
        resizeObserver.observe(placeholderRef.current);
      }
      if (navRef.current) {
        resizeObserver.observe(navRef.current);
      }
    }

    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      resizeObserver?.disconnect();
    };
  }, []);

  return { placeholderRef, navRef, isPinned, pinStyle, navHeight };
}

export function SectionNavigation({ sections, contentRef, className }: SectionNavigationProps) {
  const { t } = useTranslation();
  const [currentSection, setCurrentSection] = useState<string | null>(sections[0]?.id ?? null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const observedRef = useRef<Set<Element>>(new Set());
  const { placeholderRef, navRef, isPinned, pinStyle, navHeight } = usePinnedSectionNav();

  useEffect(() => {
    const root = contentRef?.current ?? null;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const id = entry.target.getAttribute('data-section-id');
          if (id) setCurrentSection(id);
        });
      },
      {
        root,
        rootMargin: ROOT_MARGIN,
        threshold: 0,
      },
    );

    const observeAll = () => {
      const container = contentRef?.current ?? document;
      const scope = container === document ? document.body : container;
      sections.forEach((s) => {
        const el = scope.querySelector(`[data-section-id="${s.id}"]`);
        if (el && !observedRef.current.has(el)) {
          observedRef.current.add(el);
          observerRef.current?.observe(el);
        }
      });
    };

    observeAll();
    const interval = setInterval(observeAll, 150);

    return () => {
      clearInterval(interval);
      observedRef.current.forEach((el) => observerRef.current?.unobserve(el));
      observedRef.current.clear();
      observerRef.current?.disconnect();
    };
  }, [sections.map((s) => s.id).join(','), contentRef]);

  const scrollToSection = (id: string) => {
    const container = contentRef?.current ?? document;
    const scope = container === document ? document.body : container;
    const el = scope.querySelector(`[data-section-id="${id}"]`) as HTMLElement | null;
    if (el) {
      setCurrentSection(id);
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (sections.length === 0) return null;

  return (
    <div
      ref={placeholderRef}
      className={cn('w-48 shrink-0 self-start', className)}
      style={isPinned && navHeight > 0 ? { minHeight: navHeight } : undefined}
    >
      <nav
        ref={navRef}
        aria-label={t('navigation.sections.jumpTo')}
        className={cn(
          'border-r border-border pr-4',
          'max-h-[calc(100svh-2rem)] overflow-y-auto',
          isPinned && 'fixed z-20',
        )}
        style={
          isPinned && pinStyle
            ? {
                top: pinStyle.top,
                left: pinStyle.left,
                width: pinStyle.width,
              }
            : undefined
        }
      >
        <ul className="space-y-1 text-sm">
          {sections.map((section) => {
            const Icon = section.icon;
            const isActive = currentSection === section.id;
            return (
              <li key={section.id}>
                <button
                  type="button"
                  onClick={() => scrollToSection(section.id)}
                  className={cn(
                    'w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors',
                    'hover:bg-muted hover:text-foreground',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                    isActive
                      ? 'bg-muted font-medium text-foreground'
                      : 'text-muted-foreground',
                  )}
                  aria-current={isActive ? 'true' : undefined}
                >
                  {Icon && <Icon className="h-4 w-4 shrink-0" />}
                  <span className="truncate">{t(section.labelKey)}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}

export function SectionAnchor({
  id,
  children,
  className,
  ...props
}: React.ComponentProps<'div'> & { id: string }) {
  return (
    <div
      data-section-id={id}
      id={id}
      className={cn('scroll-mt-[calc(var(--header-height,3rem)+1rem)]', className)}
      {...props}
    >
      {children}
    </div>
  );
}
